import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { boolean, object, string } from "yup";
import { createUser } from "../../../api/user";
import {
  Button,
  DocumentsCheckboxField,
  DropdownField,
  Form,
  InputField,
} from "../../../components/common";
import { PhoneInputField } from "../../../components/common/input/PhoneInput";
import { saveLoginToken } from "../../../helpers/authorizationToken";
import "./Register.scss";
import { useGetMe } from "../../../hooks";
import { FIRST_STEP } from "../../../helpers/steps";
import { useFormContext, useWatch } from "react-hook-form";

const MPOWER_SOURCE = "MPOWER Financing";

export const validationSchema = object({
  firstname: string().required("First name is a required field"),
  lastname: string().required("Last name is a required field"),
  email: string()
    .email("Please enter a valid email")
    .trim()
    .required("E-mail is a required field"),
  phone: string()
    .required("Phone is required and should be a US number")
    .min(17, "Must be a US number")
    .max(17, "Must be a US number"),
  plainPassword: string().required("Password is a required field"),
  how: string().required("Please tell us how you heard about us"),
  agreement: boolean().isTrue(
    "You must agree to the Privacy Policy & Terms of Service"
  ),
});

const Profile = ({ isReadonly = false, setPreviewState }) => {
  const navigate = useNavigate();
  const { state: prefilledData } = useLocation();
  const { data: me } = useGetMe();
  const [submitting, setSubmitting] = useState(false);
  const utm = localStorage.getItem("mpower_referral_code");

  const [defaultValues, setDefaultValues] = useState(() => {
    return {
      ...prefilledData,
      ...(utm && {
        how: MPOWER_SOURCE,
        redemptionCode: utm,
      }),
    };
  });

  useEffect(() => {
    if (isReadonly) {
      setDefaultValues({
        firstname: me?.firstname,
        lastname: me?.lastname,
        email: me?.email,
        phone: me?.phone,
        how: me?.how,
        redemptionCode: me?.redemptionCode,
        agreement: true,
      });
    }
  }, [me]);

  const handleSubmit = (data) => {
    if (isReadonly) {
      navigate("/funding-application#firstStep");
      setPreviewState(FIRST_STEP);
      return;
    }

    delete data.agreement;

    const formattedData = {
      ...data,
      phone: data.phone.replace("(", "").replace(")", "").replace(/\s/g, ""),
    };

    setSubmitting(true);

    (async () => {
      try {
        const res = await createUser(formattedData);
        if (res?.data?.token) {
          saveLoginToken(res.data.token);
          navigate("/verification");
        }
      } catch (e) {
        console.error("Error: ", e);
      }
      setSubmitting(false);
    })();
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    document.body.classList.add("register");
    return () => {
      document.body.classList.remove("register");
    };
  }, []);

  const ConditionalReferralField = () => {
    const { control } = useFormContext();
    const howValue = useWatch({ name: "how", control });
  
    if (!howValue) return null;
  
    // Only show redemption code for these three options
    const showRedemptionOptions = ["From a Friend", MPOWER_SOURCE, "Other"];
    
    if (!showRedemptionOptions.includes(howValue)) {
      return null;
    }
  
    const isMpower = howValue === MPOWER_SOURCE;
    const isOther = howValue === "Other";
    const isFromFriend = howValue === "From a Friend";
  
    const prefillValue = isMpower && utm ? utm : "";
  
    if (isOther) {
      return (
        <InputField
          id="redemptionCode"
          name="redemptionCode"
          placeholder="Feel free to describe how you heard about us"
          labelAbove="Tell us more"
          required={false}
        />
      );
    }
  
    let label = "Referral or promo code (optional)";
    let placeholder = "Enter a code if you have one";
  
    if (isMpower) {
      label = "Referral code from MPOWER Financing";
      placeholder = "Enter referral code";
    } else if (isFromFriend) {
      label = "Friend's referral code (optional)";
      placeholder = "Enter your friend's code";
    }
  
    return (
      <InputField
        id="redemptionCode"
        name="redemptionCode"
        placeholder={placeholder}
        labelAbove={label}
        readOnly={isMpower && !!utm}
        defaultValue={prefillValue}
      />
    );
  };  

  return (
    <div className="step-wrapper profile flex-column align-center">
      <h1 style={{ fontSize: "24px", fontWeight: 400, paddingTop: "2rem" }}>
        Profile Info
      </h1>
      <Form
        onSubmit={handleSubmit}
        validationSchema={isReadonly ? object({}) : validationSchema}
        defaultValues={defaultValues}
      >
        <InputField
          id="firstname"
          name="firstname"
          placeholder="Input first name"
          labelAbove="First name"
          required={!isReadonly}
          readOnly={isReadonly}
        />
        <InputField
          id="lastname"
          name="lastname"
          placeholder="Input last name"
          labelAbove="Last name"
          required={!isReadonly}
          readOnly={isReadonly}
        />
        <PhoneInputField
          id="phoneNumber"
          name="phone"
          placeholder="Phone number"
          labelAbove="Phone number"
          required={!isReadonly}
        />
        <InputField
          id="email"
          name="email"
          placeholder="example@mail.com"
          labelAbove="E-mail"
          required={!isReadonly}
          readOnly={isReadonly}
        />
        <InputField
          id="password"
          name="plainPassword"
          placeholder="Create password"
          type="password"
          labelAbove="Password"
          required={!isReadonly}
          readOnly={isReadonly}
        />
        <DropdownField
          id="how"
          name="how"
          placeholder="Tell us how you heard about us"
          labelAbove="How did you hear about us?"
          options={[
            { label: "Instagram" },
            { label: "TikTok" },
            { label: "LinkedIn" },
            { label: "X" },
            { label: "Email" },
            { label: "Blog" },
            { label: "Search Engine" },
            { label: "From a Friend" },
            { label: MPOWER_SOURCE },
            { label: "Other" },
          ]}
          dropdownLabelField="label"
          dropdownValueField="label"
          defaultValue={defaultValues?.how}
          required
          readOnly={utm && defaultValues?.how === MPOWER_SOURCE}
        />
        <ConditionalReferralField />

        <DocumentsCheckboxField
          id="agreement"
          name="agreement"
          customErrorMsg="You must agree to the Privacy Policy & Terms of Service"
          label={
            <div>
              By checking this box, I expressly consent to YELO Funding
              contacting me using the information I provide.
            </div>
          }
        />
        <Button
          className="primary full-width xl"
          type="submit"
          label="Next step"
          disabled={submitting}
        />
      </Form>
    </div>
  );
};

export default Profile;
